import { OrderToShip } from './ShippingPlanningService';
import { ProductItem, BaseProductItem } from 'src/models/Order';

// Constants
const EXCLUDED_SKU_PREFIX = 'SLUZ';
const EXCLUDED_SKU_VARESKA = 'WP81116';
const EXCLUDED_PILLOWS = ['10000014769', '10000011938'];
const SMALL_ITEM_WEIGHT_THRESHOLD = 1; // kg

export interface ProductSummaryItem {
    productCode: string;
    variants: string[];
    stockLocations: string[];
    quantity: number;
    boxCount: number;
}

export interface ProductsSummary {
    highQuantityProducts: ProductSummaryItem[];
    smallItems: ProductSummaryItem[];
}

interface ProductAggregation {
    [productCode: string]: ProductSummaryItem;
}

function hasSubItems(item: ProductItem): boolean {
    return item.subItems && item.subItems.length > 0;
}

function shouldExcludeFromList1(productCode: string): boolean {
    if (productCode.startsWith(EXCLUDED_SKU_PREFIX)) {
        return true;
    }
    if (productCode === EXCLUDED_SKU_VARESKA) {
        return true;
    }
    if (EXCLUDED_PILLOWS.includes(productCode)) {
        return true;
    }
    return false;
}

function aggregateProducts(
    items: BaseProductItem[],
    filterFn?: (item: BaseProductItem) => boolean
): ProductAggregation {
    const aggregation: ProductAggregation = {};

    items.forEach(item => {
        if (filterFn && !filterFn(item)) {
            return;
        }

        const productCode = item.productCode;

        if (!aggregation[productCode]) {
            aggregation[productCode] = {
                productCode: item.productCode,
                variants: [],
                stockLocations: [],
                quantity: 0,
                boxCount: 0
            };
        }

        aggregation[productCode].quantity += item.quantity;
        aggregation[productCode].boxCount += item.boxCount || 0;

        // Collect unique variants
        if (
            item.variant &&
            !aggregation[productCode].variants.includes(item.variant)
        ) {
            aggregation[productCode].variants.push(item.variant);
        }

        // Collect unique stock locations
        if (
            item.stockLocation &&
            !aggregation[productCode].stockLocations.includes(
                item.stockLocation
            )
        ) {
            aggregation[productCode].stockLocations.push(item.stockLocation);
        }
    });

    return aggregation;
}

function sortByQuantity(items: ProductSummaryItem[]): ProductSummaryItem[] {
    return items.sort((a, b) => b.quantity - a.quantity);
}

export function calculateProductsSummary(
    orders: OrderToShip[]
): ProductsSummary {
    const allItems: BaseProductItem[] = [];

    orders.forEach(order => {
        order.items.forEach(item => {
            if (!hasSubItems(item)) {
                allItems.push(item);
                return;
            }

            item.subItems.forEach(subItem => {
                allItems.push({
                    ...subItem,
                    quantity: subItem.quantity * item.quantity
                });
            });
        });
    });

    // List 1: High quantity products with exclusions
    const list1Items = allItems.filter(
        item => !shouldExcludeFromList1(item.productCode)
    );
    const list1Aggregation = aggregateProducts(list1Items);
    const highQuantityProducts = sortByQuantity(
        Object.values(list1Aggregation)
    );

    // List 2: Small items under 1kg
    const list2Items = allItems.filter(
        item =>
            item.weight <= SMALL_ITEM_WEIGHT_THRESHOLD &&
            !item.productCode.startsWith(EXCLUDED_SKU_PREFIX)
    );
    const list2Aggregation = aggregateProducts(list2Items);
    const smallItems = sortByQuantity(Object.values(list2Aggregation));

    return {
        highQuantityProducts,
        smallItems
    };
}
